import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useParams } from "react-router-dom";
import { Spinner } from "@radix-ui/themes";

import {
  filterCustomField,
  getTicketsFormExplorerObject,
  useExploreTickets,
} from "../../api/useExplore";
import { TTopic } from "../simulator/types";
import { useGetTopics } from "../../api/useGetTopics";
import { InstructionsProvider } from "../simulator/context/instructions";
import {
  ELocalStorage,
  fetchFromLocalStorage,
} from "../../assets/storage/localStorage";
import { saveToLocalStorage } from "../../assets/storage/localStorage";

import styles from "./styles.module.scss";
import {
  TSimulatorLiveModeReviewTicket,
  TSimulatorLiveModeTicket,
} from "./types";
import { SimulateTicket } from "./simulateTicket";
import { useWorkspace } from "../simulator/context/wrokspace";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { useExploreInteractions } from "@/v2/entities/explore/Explore.api";

export const SimulatorLiveMode = () => {
  const { topicId } = useParams();
  const queryParams = new URLSearchParams(window.location.search);
  const channel = queryParams.get("channel") || "All";
  const tenantGroup = queryParams.get("tenantGroup")?.split(",") || [];
  const customField = queryParams.get("customField")?.split(",") || [];

  const { workspace } = useWorkspace();
  const { data: topics }: { data: TTopic[] | undefined } = useGetTopics();

  const topic = useMemo(() => {
    return topics?.find((t) => t.id.toString() === topicId)?.topic ?? null;
  }, [topicId, topics]);

  const filter = useMemo<Filter | undefined>(() => {
    if (topic == null) {
      return;
    }

    const last30Days = new Date(Date.now() - 1000 * 60 * 60 * 24 * 30);

    const filter: Filter = {
      and: [
        { createdAt: { gte: last30Days.toISOString() } },
        { topic: { eq: topic } },
        ...(workspace === "notable" ? [{ partner: { in: ["Compass"] } }] : []),
      ],
    };

    return filter;
  }, [topic, workspace]);

  const { data: tickets, isLoading } = useExploreInteractions({
    source: "tickets",
    sorting: [{ id: "createdAt", desc: true }],
    filter,
    enabled: !!filter,
  });

  const [activeTicketIndex, setActiveTicketIndex] = useState<number | null>(0);

  const [reviewTickets, setReviewTickets] = useState<
    TSimulatorLiveModeReviewTicket[]
  >([]);

  useEffect(() => {
    const tickets = fetchFromLocalStorage(
      `${ELocalStorage.LIVE_TEST_MODE}-${topic}`
    );
    setReviewTickets(JSON.parse(tickets ?? "[]"));
  }, [topicId]);

  const positiveFeedback = useMemo(() => {
    return reviewTickets.filter((t) => t.good === true).length;
  }, [reviewTickets]);

  useEffect(() => {
    if (tickets && tickets.length && reviewTickets.length) {
      saveToLocalStorage(
        `${ELocalStorage.LIVE_TEST_MODE}-${topicId}`,
        JSON.stringify(reviewTickets)
      );
    }
  }, [tickets, reviewTickets, topicId]);

  const reviewedCount = useMemo(() => {
    return reviewTickets
      ? reviewTickets.filter((t) => t.good !== undefined && t.good !== null)
          .length
      : 0;
  }, [reviewTickets]);

  return (
    <InstructionsProvider>
      <div className={styles.simulatorLiveModeContainer}>
        {!isLoading &&
        tickets &&
        tickets.length > 0 &&
        tickets[activeTicketIndex!] ? (
          <SimulateTicket
            key={tickets[activeTicketIndex!].id}
            ticket={tickets[activeTicketIndex!]}
            activeTicketIndex={activeTicketIndex!}
            ticketsLength={tickets.length}
            setActiveTicketIndex={setActiveTicketIndex}
            topic={topic}
            topicId={topicId ?? null}
            ticketReview={
              reviewTickets.find(
                (t) => t.ticketId === tickets[activeTicketIndex!].id
              ) ?? { ticketId: tickets[activeTicketIndex!].id }
            }
            setTicketReview={(ticketReview: {
              ticketId: string;
              good?: boolean;
              comment?: string;
            }) =>
              setReviewTickets((prev) => [
                ...prev.filter((t) => t.ticketId !== ticketReview.ticketId),
                ticketReview,
              ])
            }
            reviewedCount={reviewedCount}
            positiveFeedback={positiveFeedback}
          />
        ) : (
          <div className={styles.loadingContainer}>
            <Spinner size="3" />
          </div>
        )}
      </div>
    </InstructionsProvider>
  );
};
